/**
 * Slash command registry and filter
 * Static definitions for / commands available in the rich input
 */

import type { SlashCommand } from '@/types/rich-input'

/**
 * All available slash commands
 */
export const SLASH_COMMANDS: readonly SlashCommand[] = [
  {
    id: 'create-policy',
    name: 'create-policy',
    label: 'Create Policy',
    description: 'Start a guided policy creation workflow',
    icon: 'FilePlus',
    category: 'policy',
  },
  {
    id: 'validate-policy',
    name: 'validate-policy',
    label: 'Validate Policy',
    description: 'Validate an existing policy for compliance',
    icon: 'CheckCircle',
    category: 'policy',
    requiresArgs: true,
    argPlaceholder: '@Policy',
  },
  {
    id: 'explain-policy',
    name: 'explain-policy',
    label: 'Explain Policy',
    description: 'Get a human-readable explanation of a policy',
    icon: 'HelpCircle',
    category: 'policy',
    requiresArgs: true,
    argPlaceholder: '@Policy',
  },
  {
    id: 'compare-policies',
    name: 'compare-policies',
    label: 'Compare Policies',
    description: 'Compare two policies side by side',
    icon: 'GitCompare',
    category: 'policy',
  },
  {
    id: 'fetch-attributes',
    name: 'fetch-attributes',
    label: 'Fetch Attributes',
    description: 'List or search available attributes',
    icon: 'Tag',
    category: 'attributes',
  },
  {
    id: 'check-compliance',
    name: 'check-compliance',
    label: 'Check Compliance',
    description: 'Check compliance status and gaps',
    icon: 'ShieldCheck',
    category: 'compliance',
  },
  {
    id: 'list-subjects',
    name: 'list-subjects',
    label: 'List Subjects',
    description: 'List subjects and users in the identity store',
    icon: 'Users',
    category: 'subjects',
  },
  {
    id: 'scan-data',
    name: 'scan-data',
    label: 'Scan Data',
    description: 'Trigger a data discovery scan',
    icon: 'Search',
    category: 'data',
  },
] as const

/**
 * Map of command ID to system prompt hint for the LLM
 */
export const COMMAND_SYSTEM_PROMPTS: Readonly<Record<string, string>> = {
  'create-policy':
    'The user invoked /create-policy. Guide them through XACML policy creation step by step. Ask about subject, resource, action, and environment attributes.',
  'validate-policy':
    'The user invoked /validate-policy. Analyze the referenced policy for correctness, completeness, and compliance. Report any issues found.',
  'explain-policy':
    'The user invoked /explain-policy. Provide a clear, human-readable explanation of what the referenced policy does, including its rules and conditions.',
  'compare-policies':
    'The user invoked /compare-policies. Help them compare two policies by examining their rules, conditions, and effects side by side.',
  'fetch-attributes':
    'The user invoked /fetch-attributes. Help them find and understand available XACML attributes. Show categories, data types, and usage.',
  'check-compliance':
    'The user invoked /check-compliance. Analyze the current policy landscape for compliance gaps and provide recommendations.',
  'list-subjects':
    'The user invoked /list-subjects. Help them explore subjects and users in the identity store and their attributes.',
  'scan-data':
    'The user invoked /scan-data. Guide them through initiating a data discovery scan. Ask about the target database connection.',
}

/**
 * Filter commands by query string (case-insensitive, matches name or label)
 */
export function filterCommands(query: string): readonly SlashCommand[] {
  if (!query.trim()) {
    return SLASH_COMMANDS
  }

  const lower = query.toLowerCase().trim()

  return SLASH_COMMANDS.filter(
    (cmd) =>
      cmd.name.toLowerCase().includes(lower) ||
      cmd.label.toLowerCase().includes(lower) ||
      cmd.description.toLowerCase().includes(lower)
  )
}

/**
 * Look up a command by its ID
 */
export function getCommandById(id: string): SlashCommand | undefined {
  return SLASH_COMMANDS.find((cmd) => cmd.id === id)
}
